import React, { PropsWithChildren } from 'react';
import { useNavigate } from 'react-router-dom';
import { Divider, IconButton, Tooltip, Typography } from '@mui/material';
import Grid from '@mui/material/Grid2';
import {
  AddPhotoAlternateOutlined as SetImageIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { listViewItemStyles } from '../../../styles';
import { ICategory } from '../../../../dataObjects/models/categories/Category';
import { TooltipStringAssets } from '../../../../assets/stringAssets';
import { ICategoryViewModel } from '../../../../dataObjects/viewModels/categoryViewModel';
import { ImageMediaDisplayForProfileImage } from '../../../controls/digitalMediaDisplays/ImageMediaDisplayForProfileImage';
import { enumDigitalMediaDisplayEnvironment, enumImageFileSize, enumPersistableObjectType } from '../../../../dataObjects/enums';


export interface CategoriesListViewItemProps extends PropsWithChildren<unknown> {
  categoryViewModel: ICategoryViewModel;
  onDelete: (category: ICategory) => void;
  onDeleteProfileImage: (categoryViewModel: ICategoryViewModel) => void;
}

export const CategoriesListViewItem: React.FC<CategoriesListViewItemProps> = (props: CategoriesListViewItemProps) => {
  CategoriesListViewItem.displayName = 'Categories List View Item';

  // eslint-disable-next-line no-console
  // console.info(`Render CategoriesListViewItem`);

  const classes: Record<string, string> = listViewItemStyles();

  const { categoryViewModel, onDelete, onDeleteProfileImage } = props;
  const { category, profileImage } = categoryViewModel;
  const { id, name, description } = category;

  // the 'useNavigate' hook will be used to navigate the browser
  const navigate = useNavigate();

  return (
    <>
      {/* <span className={classes.tableRow} key={id}> */}
      <div className={classes.tableRow} key={id}>
        {/* Data Row 1: Name & Action Buttons  */}
        <Grid container size={12} direction="row" >
          {/* Name */}
          <Grid container size={{ xs: 8, sm: 10 }} >
            <div style={{
              margin: '5px 5px 5px 0px', paddingLeft: '5px', paddingRight: '5px', paddingBottom: '8px',
              backgroundColor: `${category.displayBackground}`, border: `1px solid ${category.displayBorder}`
            }}>
              <Typography className={classes.title} variant="h6" style={{ color: `${category.displayFontColor}` }}>
                {/* Display the name with a bounding box that reflect the colors (background, border, font) */}
                {name}
              </Typography>
            </div>

          </Grid>
          {/* Action Buttons */}
          <Grid container size={{ xs: 4, sm: 2 }} direction="row" justifyContent="center" alignContent="flex-start">
            {/* Edit button */}
            <Grid container className={classes.actionButtonContainer} size={6} >
              <Tooltip title={TooltipStringAssets.edit} arrow>
                <IconButton
                  size="small"
                  className={`${classes.actionButton} ${classes.editIconButton}`}
                  onClick={() => id && navigate(`/category/${id}`)}
                >
                  <EditIcon />
                </IconButton>
              </Tooltip>
            </Grid>
            {/* Delete button */}
            <Grid container className={classes.actionButtonContainer} size={6} >
              <Tooltip
                // the tooltip for the delete button will either be "Delete" (if not the user's Uncategorized category) or "Delete disallowed" (for the user's Uncategorized category)
                title={category.id === category.ownerId ? "Delete disallowed" : "Delete"}
                arrow
              >
                <span>

                  <IconButton
                    size="small"
                    className={`${classes.actionButton} ${classes.deleteIconButton}`}
                    // this button will be disabled if the Category is the user's Uncategorized category
                    disabled={category.id === category.ownerId}
                    onClick={() => category && onDelete(category)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </span>
              </Tooltip>
            </Grid>
          </Grid>
        </Grid>
        {/* Data Row 2: Description  */}
        <Grid container size={12} >
          {(profileImage === undefined) &&
            <div className={classes.setProfileImageButtonContainer}>
              <Tooltip
                // the tooltip for the edit button will either be "Set profile image" (if user has sufficient permission) or "Set profile image disallowed" (insufficient permission)
                title={TooltipStringAssets.setProfileImage}
                arrow
              >
                <span>
                  <IconButton
                    style={{ borderRadius: '50%' }} // to make it a round button
                    className={`${classes.actionIconButton} ${classes.editIconButton}`}
                    size="medium"
                    aria-label="edit"
                    onClick={() => navigate(`/${category.id}/${enumPersistableObjectType.Category}/profileImageLink`)} >
                    <SetImageIcon fontSize='large' />
                  </IconButton>
                </span>
              </Tooltip>
            </div>
          }

          {(profileImage !== undefined) &&
            <Grid container direction={'column'} justifySelf={'center'}>
              <Grid container justifyContent={'center'} >
                <ImageMediaDisplayForProfileImage
                  imageLink={profileImage}
                  displayEnvironment={enumDigitalMediaDisplayEnvironment.CardView}
                  displayImageSize={enumImageFileSize.Small}
                  displayAsRound={true}
                />
              </Grid>
              <Grid container justifyContent={'center'} >
                <Tooltip
                  // the tooltip for the Delete Profile Image button will either be "Delete profile image" (if user has sufficient permission) or "Delete profile image disallowed" (insufficient permission)
                  title={TooltipStringAssets.deleteProfileImage}
                  arrow
                >
                  <span>
                    <IconButton
                      style={{ borderRadius: '50%', marginTop: '-30px', marginBottom: '5px', scale: '0.8' }}
                      className={`${classes.actionIconButton} ${classes.deleteIconButton}`}
                      size="small"
                      aria-label="delete profile image"
                      // this button will be disabled if the user doesn't have Edit permissions -OR- the Channel is a private Channel
                      onClick={() => onDeleteProfileImage(categoryViewModel)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              </Grid>
            </Grid>
          }

          <Typography className={classes.description} variant="body1">
            {description}
          </Typography>
        </Grid>

        {/* Add a dividing line after each item */}
        <Divider />
      </div>
      {/* </span> */}
    </>
  );

}

export default CategoriesListViewItem;